<template>
<!-- 
  - We initialize the liquor-tree instance using the default file JSON object.
  - The expected structure is hierarchy using text, id, and children. 
  - Once received results from KeywordSearch components, the liquor-tree data is updated with the results.
  - The user sees the hierarchy and can make selections.
  - Selected NAICS codes are sent to API_Directus for Detail pane
-->
  <div>
    <div :class="checkboxAppearance">
      <a tabindex="0" id="treeHeader" ref="treeHeader" href="#detailHeader" class="show-on-hover skip-text">
        {{ $t('skip_to_details') }}
      </a>
      <tree
        id="naicstree"
        ref="naicstree"
        v-model="treeModel"
        :data="treeData"
        :options="treeOptions"
        :aria-multiselectable="this.mx_toggleMultiple ? true : false"
        @node:selected="onSelect"
        @node:checked="onCheck"
        @node:unchecked="onUnCheck"
        @tree:mounted="InitTreeMount"
        @node:expanded="expandedHandler"
      >
        <div slot-scope="{ node }" tabindex="0" class="node-container" role="treeitem" :aria-expanded="node.expanded() ? true : false">
          <v-touch tag="div" v-on:swipeleft="swipeForward(node)" class="node-text">
            <span
              v-if="mx_toggleShowCode"
              class="naicscode"
              :aria-label="$t('aria_label_code')"
            >{{ node.id }} - </span>
            <span :id="'data-id-' + node.id" v-html="displayText(node.text)" :aria-label="$t('aria_label_title')"></span><br />
            <small v-if="node.examples" class="examples" v-html="displayExamples(node)"></small>
          </v-touch>
        </div>
      </tree>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
var VueTouch = require('vue-touch');
Vue.use(VueTouch, {name: 'v-touch'});
VueTouch.config.swipe = {
  direction: 'horizontal'
}
import LiquorTree from "liquor-tree";

export default {
  name: "TreeView",
  i18n: {
    locale: "en",
    messages: {
      en: {
        skip_to_details: "Skip to details",
        aria_label_code: "North Americain Industry Classification System Code",
        aria_label_title: "North Americain Industry Classification System Title"
      },
      fr: {
        skip_to_details: "Passer aux détails",
        aria_label_code: "Code du Système de classification des industries de l'Amérique du Nord",
        aria_label_title: "Titre du Système de classification des industries de l'Amérique du Nord"
      }
    }
  }, // end i18n
  props: {
    lang: {
      type: String,
      default: "en"
    },
    bpdata_newtreedata: {
      type: Array,
      default: function () {
        return []
      }
    },
    bpdata_clearall: {
      type: Boolean,
      default: false  
    },
    iduncheck: {
      type: String,
      default: null
    },
    bpdata_this: { // Getting 'this' from parent
      type: Object,
      default: function() {
        return {}
      }
    }
  }, // end props
  data: function() {
    return {
      selectValues: [],
      treeModel: null,
      treeData: this.bpdata_newtreedata.length != 0 ? this.bpdata_newtreedata : this.getInitTree,
      treeOptions: {
        multiple: this.mx_toggleMultiple,
        checkbox: true,
        parentSelect: this.mx_isMobile() ? true : false,
        autoCheckChildren: false
      },
      naicstree: this.$refs.naicstree,
      allresults: [],
      searchTerm: "",
      levelSelectable: []
    };
  }, // end data
  components: { [LiquorTree.name]: LiquorTree },
  mounted: function() {
    this.$i18n.locale = this.mx_toggleLang;
    this.levelSelectable = this.getLevelSelectable;
    let naicsLevel = ["2","3","4","5"];
    let valide = false;
    this.levelSelectable.some(level => {
      valide = naicsLevel.indexOf(level) !== -1 ? true : false;
      return naicsLevel.indexOf(level) !== -1; // break out loop if one true is found
    });
    this.levelSelectable = (valide) ? this.levelSelectable : naicsLevel;
    this.searchTerm = this.getSearchKeyword;
  }, // end mounted
  computed: {
    toggleSelection: function() {
      return typeof(this.getAllCheckedValues.naics.length) !== 'undefined' && this.getAllCheckedValues.naics.length != 0 ? this.getAllCheckedValues.naics : [];
    }, // end toggleSelection
    checkboxAppearance: function() {
      var displayClass = "";
      if (!this.multiple) {
        displayClass = "display-as-radio-button";
      }
      return displayClass;
    } // end checkboxApparearance
  }, // end computed
  watch: {
    treeModel: function() {
      if (this.treeModel.checked.length == 0) {
        this.updateHasNoSelection(true);
        if (this.mx_isMobile()) {
          this.saveSelectionMobile();
        }
        this.$emit("bpdata_selection", this.getHasNoSelection);
      } else {
        if (this.mx_isMobile()) {
          this.saveSelectionMobile();
        }
      }
    }, // end treeModel
    bpdata_newtreedata: function() {
      this.searchTerm = this.getSearchKeyword;
      if (this.bpdata_newtreedata.length > 0 &&  this.getSearchHasResults) {
        this.treeData = [];
        this.treeData = this.bpdata_newtreedata;
        this.$refs.naicstree.setModel(this.treeData);        
        let allNodes =  this.$refs.naicstree.findAll( {});
        
        // Expand the whole tree only if user have saved selection     
        if (this.getSearchHasResults || this.getAllCheckedValues["naics"].length > 0) {
           allNodes.expand();
        } else {
          this.treeData.forEach(naicsTopLevel => {           
            let node = this.$refs.naicstree.find({ id: naicsTopLevel.id });
            this.expandedHandler(node[0],true); // do only for top Level
          });   
        }

        if (this.getSearchKeyword == "" || this.getSearchKeyword === null) {
          this.resetExamplesForNodes();
        } else {
          // set Level and Examples
          this.setLevelAndExamplesForNodes();
        }
        // get results selection id and check selection in tree
        if (this.toggleSelection.length > 0) {
          this.toggleSelection.forEach(el => {
            let node = this.$refs.naicstree.find({ id: el.code });
            
            if (node.length > 0) {
              node.check();
            }
          }); // end foreach
        } // endif toggleSelection
      } else {
        if (this.treeModel != null && this.treeModel.checked.length == 0) {
          if (this.searchTerm.length > 0) {
            this.$refs.naicstree.setModel(); // empty tree
          } else {
            this.treeData = this.getSavedTree;
            this.$refs.naicstree.setModel(this.treeData); // default tree
            // init loading       
            this.treeData.forEach(topLevel => {
              let node = this.$refs.naicstree.find({ id: topLevel.id });        
              if (node[0].parent == null) {
                this.expandedHandler(node[0],true); // do only for top Level
              }    
            });            
          }
        } // endif treemodel
      }
    }, // end bpdata_newtreedata
    bpdata_clearall: function() {
      if (this.bpdata_clearall) {
        this.clear();
      }
    }, // end bpdata_clearall
    bpdata_itemselected: function() {
      // check box in tree if checked first in detail
      if (this.bpdata_itemselected.length != 0) { 
        this.bpdata_itemselected.forEach(element => {
          let node = this.$refs.naicstree.find({ id: element.code });
          if (node.length > 0) {
            node.check();
          }
        }); // end foreach
      }
    }, // end itemselected
    iduncheck: function() {
      if (this.iduncheck != "") { 
        let unchecknode = this.$refs.naicstree.find({ id: this.iduncheck });
        if (unchecknode.length > 0) {
          unchecknode.uncheck();
        }
      }
    }, // end iduncheck
    lang: function() {
      this.$i18n.locale = this.mx_toggleLang;
    } // end lang   
  }, // end watch
  methods: {
    swipeForward: function(node) {
      if (this.mx_isMobile()) {
        this.onSelect(node);
        this.bpdata_this.$refs.tinySlider.slider.goTo(1);       
      }
    }, // end swipeForward
    saveSelectionMobile: function() {
      this.clearedkeyword = true;
      this.editClicked = false;
      this.updateAllSaved({
        key: "naics",
        payload: this.getAllCheckedValues['naics']
      });
      this.updateSavedTree(this.getReducedTree);
      this.$emit("selection", false);
      this.$emit("save", this.getAllSavedValues);
      // track naics selected by users
      this.getAllSavedValues.naics.forEach(naics => {
        this.$gtag.customMap({ 'dimension3': "keyword", "dimension2": "naics" });
        this.$gtag.event("industry-selected", {
          event_category: "naics-tree-widget",
          event_label: naics.name,
          keyword: this.searchTerm,
          naics: naics.code,
          product: process.env.VUE_APP_PRODUCT
        });
      });
    }, // end saveSelection
    InitTreeMount: function() {
      this.treeData = this.getSavedTree;
      this.$refs.naicstree.setModel(this.treeData);  
      this.updateReducedTree(this.treeData);
      let allNodes = this.$refs.naicstree.findAll({ state: { expanded: false } });
      // get default selection and check selection in tree
      if (this.getAllSavedValues.naics.length > 0) {
        if(this.treeData.length< 20){
          //meant user did a search
          allNodes.expand();
        } else {
          // initial remove level only for top level 
          allNodes.forEach(node => {
            if (node.parent == null) {
              this.expandedHandler(node,true); // do only for top Level
            }    
          });

          // expand only saved Values
          // have to expand all level until reach the checked level
          this.getAllSavedValues.naics.forEach(element => {
            let elementCode = element.code;
            for (let i = 1; i < elementCode.length; i++) {
              let newCode = elementCode.substring(0, i)
              if (newCode.length == 2) {
                // map to Top Level because some have id like this 31-33
                newCode = this.getMapTopLevelNaics[newCode];
              }

              let node = this.$refs.naicstree.find({ id: newCode });
              if (node.length > 0) {
                node.expand();
              }
            } // end for
          }); // end foreach
        } // end else

        this.setLevelAndExamplesForNodes();

        // check selection
        this.getAllSavedValues.naics.forEach(element => {
          let node = this.$refs.naicstree.find({ id: element.code });
          if (node.length > 0) {
            node.check();
          }
        }); // end foreach

        this.updateHasNoSelection(false);
        this.$emit("bpdata_selection", this.getHasNoSelection);
      } else {
        // init loading       
        allNodes.forEach(node => {
          if (node.parent == null) {
            this.expandedHandler(node,true); // do only for top Level
          }    
        });
      } // endif getAllSavedValues.naics
    }, // end InitTreeMount
    onSelect: function(node) {
      this.$gtag.customMap({ 'dimension3': "keyword", "dimension2": "naics" });
      this.$gtag.event("industry-detail-description", {
        event_category: "naics-tree-widget",
        event_label: node.text,
        keyword: this.searchTerm,
        naics: node.id,
        product: process.env.VUE_APP_PRODUCT
      });
      this.updateSelectedNode(node);
      this.$emit("getDetail", node.id);
      this.updateShowDetail(true);
      this.setAriaSelected(node,true);

      node.unselect();
    }, // end onSelect
    onCheck: function(node) {
      // save selected values
      if (node != null) {
        // get checkobx element
        // for accessibility
        this.setAriaChecked(node,true);
        this.setAriaSelected(node,true);
        var index = this.selectValues.findIndex(x => x.code == node.id);

        if (index === -1) {
          if (this.multiple) {
            this.selectValues.push({
              code: node.id,
              name: node.text,
              examples: node.examples
            });
          } else {
            // not multiple
            this.selectValues[0]= {
              code: node.id,
              name: node.text,
              examples: node.examples
            };
          }
          this.updateAllChecked({
            key: "naics",
            payload: this.selectValues
          });
          this.updateHasNoSelection(false);
        }
      } else {
        // null
        this.selectValues = [];
        this.naicsSelect = [];
        this.updateHasNoSelection(true);
      }
       
      // testing, if mobile, stay on the tree view
      // when user clicked on checkbox
      if (!this.mx_isMobile()) {
        node.select();
      }

      this.$emit("bpdata_selection", this.getHasNoSelection);
      node.unselect();

      // fix checking one box when single
      if (!this.multiple) { 
        this.$refs.naicstree.checked().forEach(checkedNode => {
          if (checkedNode !== node) {
            checkedNode.uncheck();
          }
        });
      }
    }, // end onCheck
    onUnCheck: function(val) {      
      // get checkobx element
      // for accessibility
      this.setAriaChecked(val,false);      

      // fix to make sure if single
      // user can't uncheck radio button using mouse
      if (!this.multiple && !this.bpdata_clearall) {
        let checkedVAlues = this.getAllCheckedValues; 
        if (checkedVAlues.naics.length > 0) {
          if (val.id == checkedVAlues.naics[0].code){
            val.check();                
          }     
        }        
      } else {
        if (val != null) {
          var ind = this.selectValues.findIndex(function(i) {
            return i.code == val.id;
          });
          if (ind !== -1){
            this.selectValues.splice(ind, 1);
          }
        } else {
          // null
          this.selectValues = [];
          this.naicsSelect = [];
          this.clearSavedSelectTree();
          this.updateHasNoSelection(true);
        }
        // testing, if mobile, stay on the tree view
        // when user clicked on checkbox
        if (!this.mx_isMobile()) {
          val.select();
        }
        // send back new values
        this.updateAllChecked({
          key: "naics",
          payload: this.selectValues
        });
        this.$emit("bpdata_selection", this.getHasNoSelection);

        val.unselect();
      } // end if not multiple
    }, // end onUnCheck
    clear: function() {
      // uncheck all
      this.$refs.naicstree.checked().forEach(checkedNode => {
        checkedNode.uncheck();
      });
      // reset variables to null
      this.selectValues = [];
      this.naicsSelect = [];
      this.updateHasNoSelection(true);
      this.$emit("cleared", true);
    }, // end clear
    displayText: function(node) {
      let keyword = this.getSearchKeyword;
      if (keyword != "") {
        let regex = new RegExp(keyword, 'gi');
        return node.replace(regex, '<span class="highlight">$&</span>');
      } else {
        return node;
      }
    }, // end displayText
    displayExamples: function(node) {
      let output = "<ul role='list' class='tree-examples'>";
      let numberOfExamples = (node.examples.length > 3) ? 3 : node.examples.length; // limit to 3 examples

      // highlight keyword matches in the examples
      for (let i = 0; i < numberOfExamples; i++) {
        let example = node.examples[i].example; 
        // since match words are tag with <em>, match with those < and >
        let regex = new RegExp("<.*?>", 'gi');

        if (example.example) {
          output += "<div class='example'><li role='listitem'>" + example.example.replace(regex, '<span class="highlight">$&</span>') + "</li></div>";
        } else {
          output += "<div class='example'><li role='listitem'>" + example.replace(regex, '<span class="highlight">$&</span>') + "</li></div>";
        } 
      } // end for

      return output+"</ul>";
    }, // end displayExamples
    setLevelAndExamplesForNodes: function() {
      let allNodes = this.$refs.naicstree.findAll({});
      // add level
      allNodes.forEach(node => {
        // Top Level because some can have 31-33
        let mapNaics = node.id.split("-")[0]; // we just want to tell it is a top level, to care about the value
        node.level = mapNaics.length == 6 ? "5" : (mapNaics.length).toString();
      }); // end foreach
      // Triage examples by Naics
      this.examplesByNaics = {};
      let algoliaHits = this.getAlgoliaHitsResult;

      algoliaHits.forEach(hit => {
        let naicsCode = hit.naics;
        if (!this.examplesByNaics[naicsCode]) {
          this.examplesByNaics[naicsCode] = [];
        }
        this.examplesByNaics[naicsCode].push({ "id": hit.objectID, "example": hit._highlightResult.example.value });
      }); // end foreach
      if (this.toggleSelection.length != 0) {
        this.toggleSelection.forEach(el => {
          let naicsId = el.code;
          if (!this.examplesByNaics[naicsId]) {
            this.examplesByNaics[naicsId] = [];
          }
          for (var i in el.examples) {
            var exind = this.examplesByNaics[naicsId].findIndex(function(y) {
              return y.id == el.examples[i].id;
            });
            if (exind === -1) {
              this.examplesByNaics[naicsId].push({ "id": el.examples[i].id, "example": el.examples[i].example });
            }
          }
        }); // end foreach
      } // end if bpdata_selectionid

      // Append the examples from Algolia
      Object.entries(this.examplesByNaics).map(element => {
        let naicsCode = element[0];
        let value = element[1]; // array of examples
        let targetNode = this.$refs.naicstree.find({ id: naicsCode });
        if (targetNode.length > 0) {   
          targetNode[0].examples = value;
        }

        // add total examples count to Level 2 nodes from all levels 
        // if they have
        let level2 = naicsCode[0] + naicsCode[1];
        level2 = this.getMapTopLevelNaics[level2];
        // map to Top Leve because some have id liek this 31-33
        targetNode = this.$refs.naicstree.find({ id: level2 });
        if (targetNode.length > 0) {   
          let previousCount = (targetNode[0].countexamples) ? targetNode[0].countexamples : 0;
          targetNode[0].countexamples = previousCount + value.length;
        }
      }); // end entries     
      // sort tree descending
      this.sortTreeByExamples("desc",this.getMapTopLevelNaics);
      // save examples
      this.updateExampleByNaics(this.examplesByNaics);
    }, // end setLevelAndExamplesForNodes
    resetExamplesForNodes: function() {
      this.examplesByNaics = {};
      this.allresults.forEach(hit => {
        let naicsCode = hit.naics;
        if (!this.examplesByNaics[naicsCode]) {
          this.examplesByNaics[naicsCode] = [];
        }
        this.examplesByNaics[naicsCode].push({ "id": hit.objectID, "example": hit.example });
      }); // end foreach
      // Append the examples from Algolia
      Object.entries(this.examplesByNaics).map(element => {
        let naicsCode = element[0];
        let value = element[1]; // array of examples
        let targetNode = this.$refs.naicstree.find({ id: naicsCode });
        if (targetNode.length > 0) {   
          targetNode[0].examples = value;
        }

        // add total examples count to Level 2 nodes from all levels 
        // if they have
        let level2 = naicsCode[0] + naicsCode[1];
        level2 = this.getMapTopLevelNaics[level2];
        // map to Top Leve because some have id liek this 31-33
        targetNode = this.$refs.naicstree.find({ id: level2 });
        if (targetNode.length > 0) {   
          let previousCount = (targetNode[0].countexamples) ? targetNode[0].countexamples : 0;
          targetNode[0].countexamples = previousCount + value.length;
        }
      }); // end entries
      // save examples
      this.updateExampleByNaics(this.examplesByNaics);
    }, // end resetExamplesForNodes
    sortTreeByExamples: function(order,topNaicsLevelMapping) {
      // sorting tree by total numbers of examples 
      // map top level because some have 31-33
      function sort(node0, node1) {
        node0 = (topNaicsLevelMapping[node0]) ? (topNaicsLevelMapping[node0]) : node0;
        node1 = (topNaicsLevelMapping[node1]) ? (topNaicsLevelMapping[node1]) : node1;
        let r = (node0.countexamples - node1.countexamples);
        let p = 'asc' == order ? 1 : -1;           
        if (isNaN(r)) {
          return 0;
        }
        return r * p;
      }
      this.$refs.naicstree.sortTree(sort);
    }, // end sortTreeByExamples
    expandedHandler: function(node,topLevelOnly) {
      let naicsLevel = ["2","3","4","5"];
      let levelselectable = this.getLevelSelectable;
      let expandedLevel = [];
      for (let i = levelselectable[0]; i < 6; i++) {
        expandedLevel.push(i.toString());
      }
      let hideLevels = naicsLevel.filter(x => expandedLevel.indexOf(x) === -1);
      this.removeLevel(node, hideLevels,topLevelOnly);
    }, // end expandedHandler
    waitForElm: function(selector) {
      return new Promise(resolve => {
        if (document.querySelector(selector)) {
          return resolve(document.querySelector(selector));
        }
        const observer = new MutationObserver(mutations => {
          if (document.querySelector(selector)) {
            resolve(document.querySelector(selector));
            observer.disconnect();
          }
        });
        observer.observe(document.body, {
          childList: true,
          subtree: true
        });
      });
    }, // end waitForElm
    removeLevel: function(node, removeLevel,topLevelOnly) {
      let mapNaics = node.id.split("-")[0]; // we just want to tell is a top level, dont care about the value
      node.level = mapNaics.length == 6 ? "5" : `${mapNaics.length}`;
      let hideCheckbox = removeLevel.indexOf(node.level) >= 0 ? true: false;
        
        let query = `[data-id='${node.id}'] i.tree-checkbox`;
        this.waitForElm(query).then((elm) => {
          (hideCheckbox) ? elm.classList.add("level-hidding") : elm.classList.remove("level-hidding");    
        })
      this.accessibleCheckbox(node);
 
      if (!topLevelOnly) {
        if (node.children) {
          node.children.forEach(childNode => {
            this.removeLevel(childNode,removeLevel,topLevelOnly);
          }); // end foreach
        } // end if children
      }
    }, // end removeLevel
    accessibleCheckbox: function(node) {
      // event for checkbox
      let queryCheckbox = `[data-id='${node.id}'] i.tree-checkbox`;
      this.waitForElm(queryCheckbox).then((elm) => {      
        elm.setAttribute("role", "checkbox");
        elm.setAttribute("tabindex", "0");
        this.setAriaChecked(node,false);

        // to make sure to only add one event for keydown
        const eventExist = elm.getAttribute("listener");

        if (eventExist === null) {
          elm.setAttribute('listener', 'true');
          elm.addEventListener("keydown", function(event) {
            // Number 13 is the "Enter" key on the keyboard
            if (event.keyCode === 13) {
              event.preventDefault();
              elm.click();
              document.getElementById("detailHeader").focus();
            } // end if
          }, true); // end listener
        } // end if null
      }); // end waitforElm

      // event for title of checkbox
      let queryTitle = `[data-id='${node.id}'] div.node-container`;
      this.waitForElm(queryTitle).then((elm) => {
        this.setAriaSelected(node,false);
        // to make sure to only add one event for keydown
        const eventExist = elm.getAttribute("listener");
        if (eventExist === null) {
          elm.setAttribute('listener', 'true');
          elm.addEventListener("keydown", function(event) {
            // Number 13 is the "Enter" key on the keyboard
            if (event.keyCode === 13) {
              event.preventDefault();
              // used click() instead of node.select() to that we can expand parent              
              elm.click();
              document.getElementById("detailHeader").focus();
            }
          }, true); // end anonymous functions
        } // end null
      }); // end waitforElm
    }, // end accessibleCheckbox
    setAriaChecked: function(node,val) {
      let queryCheckbox = `[data-id='${node.id}'] i.tree-checkbox`;
      this.waitForElm(queryCheckbox).then((elm) => {   
        elm.setAttribute("aria-checked", val);
      });
    }, // end setAriaChecked
    setAriaSelected: function(node,val) {
      let queryTitle = `[data-id='${node.id}'] .node-text`;
      this.waitForElm(queryTitle).then((elm) => {   
        elm.setAttribute("aria-selected", val);
      });
    } // end setAriaSelected
  } // end methods
}; // end export default
</script>

<style>
.open-services-tree-naics-lookup .treelabel {
  font-size: 1.2em;
  font-weight: bold;
}
.open-services-tree-naics-lookup .highlight {
  font-weight: 500;
  background-color: #fdfae0;
}
.open-services-tree-naics-lookup em {
  font-weight: 500;
  background-color: #fdfae0;
}
.open-services-tree-naics-lookup .tree-node {
  white-space: normal;
  line-height: 12px;
}
.open-services-tree-naics-lookup .tree-content {
  padding: 3px 0px;
  align-items: flex-start;
  line-height: 1.2em;
}
.open-services-tree-naics-lookup .tree-checkbox {
  flex-shrink: 0;
  position: relative;
  width: 24px;
  height: 24px;
  margin-top: 4px;
  box-sizing: border-box;
  border: 1px solid #218eff;
  border-radius: 4px;
  background: rgb(255, 255, 255);
  transition: border-color .25s,background-color .25s;
  box-shadow: inset 2px 2px 3px rgba(21, 50, 83, 0.2);
}
.open-services-tree-naics-lookup .tree-arrow.has-child {
  width: 28px;
}
.open-services-tree-naics-lookup .tree-arrow {
  margin-top: 3px;
  height: 24px;
  width: 5px;
  margin-left: 28px;
}
.open-services-tree-naics-lookup .tree-checkbox.checked:after {
  border: 3px solid #fff;
  border-left: 0;
  border-top: 0;
  left: 6px;
  top: -1px;
  transform: rotate(40deg) scaleY(1);
  transition: none;
}
.open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox {
  border-radius: 20px;
  background: #fff;
  box-shadow: inset 0px 0px 3px rgba(21, 50, 83, 0.2);
}
.open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox:after {
  border: none;
  height: 12px;
  width: 12px;
  background: #eee;
  border-radius: 20px;
  left: 5px;
  top: 5px;
}
.open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox:hover {
  border-color: #218eff;
}
.open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox.checked {
  background-color: #fff;
  border-color: #218eff;
}
.open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox.checked:after {
  border: none;
  height: 12px;
  width: 12px;
  background: #218eff;
  border-radius: 20px;
  left: 5px;
  top: 5px;
}
.open-services-tree-naics-lookup .tree-content .example {
  margin: 0;
  padding: 2px 0px;
  line-height: 1.2em;
  font-weight: 400;
  color: rgb(114, 114, 114);
}
.open-services-tree-naics-lookup ul.tree-examples {
  margin-left: 1.5em;
  list-style: disc;
}
.open-services-tree-naics-lookup .tree-root > .tree-node > .tree-content {
  font-weight: 600;
}
.open-services-tree-naics-lookup .tree-root > .tree-node .tree-node > .tree-content {
  font-weight: 500;
}
.open-services-tree-naics-lookup .tree-root > .tree-node .tree-node .tree-node > .tree-content {
  font-weight: 400;
}
.open-services-tree-naics-lookup .tree-children {
  transition: unset;
}
.open-services-tree-naics-lookup .naicscode {
  font-weight: 400 !important; 
  color: rgb(99, 99, 99);
  vertical-align: bottom;
}
.open-services-tree-naics-lookup .level-hidding {
  display: none !important;
}
.tree-node > div > .tree-arrow {
  width: 0px;
}
.tree-node.has-child > div > .tree-arrow {
  width: 28px;
}
.open-services-tree-naics-lookup .skip-text {
  font-size: 90%;
}
.open-services-tree-naics-lookup .show-on-hover {
  opacity: 0;
  width: 0;
  height: 0;
  line-height: 0;
  position: absolute;
}
.open-services-tree-naics-lookup .show-on-hover:focus {
  opacity: 1;
  width: 20%;
  display: inline-block;
  height: 18px;
  line-height: 18px;
  position: relative;
}
@media only screen and (max-width: 767px) {
  .open-services-tree-naics-lookup {
    font-size: 1.0em;
  }
  .open-services-tree-naics-lookup .tree-checkbox {
    width: 20px;
    height: 20px;
    margin-top: 5px;
    margin-right: 0;
  }
  .open-services-tree-naics-lookup .tree-content {
    padding: 0px 0px;
    line-height: 1em;
  }
  .open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox:after {
    left: 3px;
    top: 3px;
  }
  .open-services-tree-naics-lookup .display-as-radio-button .tree-checkbox.checked:after {
    left: 3px;
    top: 3px;
  }
  .open-services-tree-naics-lookup ul.tree-examples {
    margin-left: 0px;
    padding-left: 0px;
  }
}
</style>
